import agencyBillingStatusFactory from "./agencyBillingStatus/agencyBillingStatusFactory.js";
import Company from "./Company.js";
import ContactInfo from "./ContactInfo.js";
import Model from "./Model.js";
import ReportDisclaimer from "./ReportDisclaimer.js";
import UnspecifiedAgencyBillingStatus from "./agencyBillingStatus/UnspecifiedAgencyBillingStatus.js";
import UploadedFile from "./UploadedFile.js";

/**
 * Agency model.
 *
 * About licenses: The current economic model is that clients pay for packages that contain a specific number of
 * licenses. Each license is for importing a company.
 * Note that currently, we don't limit the creation of company. Due to that, one can add companies even if no more
 * licenses are available.
 *
 * @param {string} id - Unique identifier
 * @param {string} referentialApiId - id of the agency in ReferentialAPI
 * @param {string} referentialApiId - id of the country the agency belongs to in ReferentialAPI
 * @param {string} name - Long name of the agency
 * @param {string} [shortName] - The short name is actually equivalent to id, it is kept for server model consistency
 * @param {string} homepage - An URL
 * @param {contactInfo} [contactInfo] - Info of the company contact
 * @param {string} defaultLanguage - Default language for the agency
 * @param {string} defaultChartOfAccountsId - Default chart of accounts for the agency
 * @param {string} countryId - The id of the country the receiver belongs to
 * @param {company[]} [companies] - Basic info about companies belonging to the agency
 * @param {boolean} isManageUsersInUiDisabled - Return true if the agency allows storing users in our database
 * @param {boolean} isLiquidityEnabled - True if the agency should see the liquidity report
 * @param {boolean} isLightEnabled - True if light-companies can be created (in Import)
 * @param {UploadedFileInfo} documentArchiveStructureAttachment - Some file metadata to represent the default document archive structure
 * @param {boolean} hasDocumentArchiveStructureAttachment - True if the agency has a document archive structure attachment
 * @param {string} defaultCompanyTypeId - Identifier of a company type this is used for every company this has none
 * @param {BillingStatus} billingStatus - Specifies whether the agency is billed
 * @param {string} [billingReference] - Specifies some text used by the people doing the invoicing
 * @param {ReportDisclaimer} [reportDisclaimer] - Report disclaimer set for the agency
 * @param {string} [displayNameForMailNotifications] - Spoofed name for the "From:" field when sending emails
 * @param {boolean}[isFortnoxLinkedDocumentsEnabled] - True if the agency is able to import Fortnox attachments, it is meant to deal with performance issues in case of a company having too many attachments
 * @param {number}[numberOfLicenses] - Number of licenses that the client is currently paying for
 * @param {boolean}[isForecastToBudgetEnabled] - True if the agency should allow converting forecasts to budgets
 * @param {boolean}[isEarlyAccessEnabled] - True if the agency should see the early access features
 * @param {boolean}[isAdvancedThemingEnabled] - True if the advanced theming settings can be saved
 * @param {boolean}[isArchiveCompanyEnabled] - True if companies can be archived for this agency
 */
export default class Agency extends Model {
	constructor(spec) {
		super(...arguments);
		this._id = spec.id || spec.shortName;
		this._referentialApiId = spec.referentialApiId;
		this._referentialApiCountryId = spec.referentialApiCountryId;
		this._name = spec.name;
		this._shortName = spec.shortName;
		this._homepage = spec.homepage;
		this._contactInfo = spec.contactInfo;
		this._countryId = spec.countryId;
		this._defaultLanguage = spec.defaultLanguage;
		this._defaultChartOfAccountsId = spec.defaultChartOfAccountsId;
		this._companies = spec.companies;
		this._isManageUsersInUiDisabled = spec.isManageUsersInUiDisabled;
		this._isLiquidityEnabled = spec.isLiquidityEnabled;
		this._isBudgetEditorEnabled = spec.isBudgetEditorEnabled;
		this._isKeyFigureEditorEnabled = spec.isKeyFigureEditorEnabled;
		this._isDocumentsEnabled = spec.isDocumentsEnabled;
		this._isCustomReportsEnabled = spec.isCustomReportsEnabled;
		this._isCoAEditorEnabled = spec.isCoAEditorEnabled;
		this._numberOfLicenses = spec.numberOfLicenses;

		this._isFortnoxLinkedDocumentsEnabled =
			spec.isFortnoxLinkedDocumentsEnabled;

		this._isReportTemplateForStandardReportsEnabled =
			spec.isReportTemplateForStandardReportsEnabled;

		this._isBimestersEnabled = spec.isBimestersEnabled;

		this._isAcknowledgePeriodReportEnabled =
			spec.isAcknowledgePeriodReportEnabled;

		this._isPeriodReportFeatureEnabled = spec.isPeriodReportFeatureEnabled;
		this._isActivePeriodFeatureEnabled = spec.isActivePeriodFeatureEnabled;

		this._isPeriodReportNotificationFeatureEnabled =
			spec.isPeriodReportNotificationFeatureEnabled;

		this._isDocumentArchiveNotificationFeatureEnabled =
			spec.isDocumentArchiveNotificationFeatureEnabled;

		this._isReportDisclaimerEnabled = spec.isReportDisclaimerEnabled;

		this._isLrfkFarmerKeyFiguresEnabled =
			spec.isLrfkFarmerKeyFiguresEnabled;

		this._shouldRedirectToAgencyOnLogout =
			spec.shouldRedirectToAgencyOnLogout;

		this._isNavigationToMyCompaniesFromAccountingEnabled =
			spec.isNavigationToMyCompaniesFromAccountingEnabled;

		this._isLightEnabled = spec.isLightEnabled;

		this._isShowPresentationEnabledForLightCompanies =
			spec.isShowPresentationEnabledForLightCompanies;

		this._isShowLightEnabledForStandardCompanies =
			spec.isShowLightEnabledForStandardCompanies;

		this._hasDocumentArchiveStructureAttachment =
			spec.hasDocumentArchiveStructureAttachment;

		this._documentArchiveStructureAttachment =
			spec.documentArchiveStructureAttachment;

		this._defaultCompanyTypeId = spec.defaultCompanyTypeId;
		this._reportDisclaimer = spec.reportDisclaimer;
		this._brandingLogo = spec.brandingLogo;

		this._billingStatus =
			spec.billingStatus || UnspecifiedAgencyBillingStatus.instance();

		this._billingReference = spec.billingReference || "";

		this._displayNameForMailNotifications =
			spec.displayNameForMailNotifications || "";

		this._isProvisionedByOneClick = spec.isProvisionedByOneClick;

		this._isAgencyPortalLinkEnabled = spec.isAgencyPortalLinkEnabled;

		this._isAccountGroupsUsageInKpiEditorEnabled =
			spec.isAccountGroupsUsageInKpiEditorEnabled;

		this._isForecastingEnabled = spec.isForecastingEnabled;

		this._isArchiveCompanyEnabled = spec.isArchiveCompanyEnabled;

		this._isForecastToBudgetEnabled = spec.isForecastToBudgetEnabled;
		this._isEarlyAccessEnabled = spec.isEarlyAccessEnabled;

		this._isAdvancedThemingEnabled = spec.isAdvancedThemingEnabled;
	}

	getId() {
		return this._id;
	}

	getLegacyId() {
		return this.getId();
	}

	getReferentialApiId() {
		return this._referentialApiId;
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getShortName() {
		return this._shortName;
	}

	setShortName(newShortName) {
		this._shortName = newShortName;
	}

	getBrandingLogoUrl() {
		return this._brandingLogoUrl;
	}

	getHomepage() {
		return this._homepage;
	}

	setHomepage(newHomepage) {
		this._homepage = newHomepage;
	}

	getContactInfo() {
		return this._contactInfo;
	}

	setContactInfo(newContactInfo) {
		this._contactInfo = newContactInfo;
	}

	getCountryId() {
		return this._countryId;
	}

	getDefaultLanguage() {
		return this._defaultLanguage;
	}

	getNumberOfLicenses() {
		return this._numberOfLicenses;
	}

	setDefaultLanguage(newDefaultLanguage) {
		this._defaultLanguage = newDefaultLanguage;
	}

	getDefaultChartOfAccountsId() {
		return this._defaultChartOfAccountsId;
	}

	setDefaultChartOfAccountsId(newDefaultChartOfAccountsId) {
		this._defaultChartOfAccountsId = newDefaultChartOfAccountsId;
	}

	getCompanies() {
		return this._companies;
	}

	getDefaultCompanyTypeId() {
		return this._defaultCompanyTypeId;
	}

	setDefaultCompanyTypeId(newDefaultCompanyTypeId) {
		this._defaultCompanyTypeId = newDefaultCompanyTypeId;
	}

	getReportDisclaimer() {
		return this._reportDisclaimer;
	}

	getBillingStatus() {
		return this._billingStatus;
	}

	setBillingStatus(billingStatus) {
		this._billingStatus = billingStatus;
	}

	getBillingReference() {
		return this._billingReference;
	}

	setBillingReference(billingReference) {
		this._billingReference = billingReference;
	}

	getDisplayNameForMailNotifications() {
		return this._displayNameForMailNotifications;
	}

	setDisplayNameForMailNotifications(spoofedName) {
		this._displayNameForMailNotifications = spoofedName;
	}

	hasDocumentArchiveStructureAttachment() {
		return this._hasDocumentArchiveStructureAttachment;
	}

	getDocumentArchiveStructureAttachment() {
		return this._documentArchiveStructureAttachment;
	}

	setDocumentArchiveStructureAttachment(attachment) {
		this._documentArchiveStructureAttachment = attachment;
	}

	isLiquidityEnabled() {
		return this._isLiquidityEnabled;
	}

	setLiquidity(bool) {
		this._isLiquidityEnabled = bool;
	}

	isBudgetEditorEnabled() {
		return this._isBudgetEditorEnabled;
	}

	setBudgetEditor(bool) {
		this._isBudgetEditorEnabled = bool;
	}

	isKeyFigureEditorEnabled() {
		return this._isKeyFigureEditorEnabled;
	}

	setKeyFigureEditor(bool) {
		this._isKeyFigureEditorEnabled = bool;
	}

	isDocumentsEnabled() {
		return this._isDocumentsEnabled;
	}

	setDocuments(bool) {
		this._isDocumentsEnabled = bool;
	}

	isCoAEditorEnabled() {
		return this._isCoAEditorEnabled;
	}

	setCoAEditor(bool) {
		this._isCoAEditorEnabled = bool;
	}

	isCustomReportsEnabled() {
		return this._isCustomReportsEnabled;
	}

	setCustomReports(bool) {
		this._isCustomReportsEnabled = bool;
	}

	isBimestersEnabled() {
		return this._isBimestersEnabled;
	}

	setBimesters(bool) {
		this._isBimestersEnabled = bool;
	}

	isPeriodReportFeatureEnabled() {
		return this._isPeriodReportFeatureEnabled;
	}

	setPeriodReportFeature(bool) {
		this._isPeriodReportFeatureEnabled = bool;
	}

	isAcknowledgePeriodReportEnabled() {
		return this._isAcknowledgePeriodReportEnabled;
	}

	setAcknowledgePeriodReport(bool) {
		this._isAcknowledgePeriodReportEnabled = bool;
	}

	isActivePeriodFeatureEnabled() {
		return this._isActivePeriodFeatureEnabled;
	}

	setActivePeriodFeature(bool) {
		this._isActivePeriodFeatureEnabled = bool;
	}

	isPeriodReportNotificationFeatureEnabled() {
		return this._isPeriodReportNotificationFeatureEnabled;
	}

	setPeriodReportNotificationFeature(bool) {
		this._isPeriodReportNotificationFeatureEnabled = bool;
	}

	isDocumentArchiveNotificationFeatureEnabled() {
		return this._isDocumentArchiveNotificationFeatureEnabled;
	}

	setDocumentArchiveNotificationFeature(bool) {
		this._isDocumentArchiveNotificationFeatureEnabled = bool;
	}

	isReportDisclaimerEnabled() {
		return this._isReportDisclaimerEnabled;
	}

	setReportDisclaimer(bool) {
		this._isReportDisclaimerEnabled = bool;
	}

	isLrfkFarmerKeyFiguresEnabled() {
		return this._isLrfkFarmerKeyFiguresEnabled;
	}

	setLrfkFarmerKeyFigures(bool) {
		this._isLrfkFarmerKeyFiguresEnabled = bool;
	}

	shouldRedirectToAgencyOnLogout() {
		return this._shouldRedirectToAgencyOnLogout;
	}

	setRedirectToAgencyOnLogout(bool) {
		this._shouldRedirectToAgencyOnLogout = bool;
	}

	isNavigationToMyCompaniesFromAccountingEnabled() {
		return this._isNavigationToMyCompaniesFromAccountingEnabled;
	}

	setNavigationToMyCompaniesFromAccounting(bool) {
		this._isNavigationToMyCompaniesFromAccountingEnabled = bool;
	}

	isLightEnabled() {
		return this._isLightEnabled;
	}

	setLightFeature(bool) {
		this._isLightEnabled = bool;
	}

	isManageUsersInUiEnabled() {
		return !this._isManageUsersInUiDisabled;
	}

	setManageUsersInUi(bool) {
		this._isManageUsersInUiDisabled = !bool;
	}

	canManageAgencyUsers() {
		return this.isManageUsersInUiEnabled() || this.isOneClickAgency();
	}

	canAddOrDeleteAgencyUsers() {
		return this.isManageUsersInUiEnabled();
	}

	canManageCompanyUsers() {
		return this.isManageUsersInUiEnabled();
	}

	isShowPresentationEnabledForLightCompanies() {
		return this._isShowPresentationEnabledForLightCompanies;
	}

	setShowPresentationForLightCompanies(bool) {
		this._isShowPresentationEnabledForLightCompanies = bool;
	}

	isShowLightEnabledForStandardCompanies() {
		return this._isShowLightEnabledForStandardCompanies;
	}

	setShowLightForStandardCompanies(bool) {
		this._isShowLightEnabledForStandardCompanies = bool;
	}

	isOneClickAgency() {
		return this._isProvisionedByOneClick;
	}

	isFortnoxLinkedDocumentsEnabled() {
		return this._isFortnoxLinkedDocumentsEnabled;
	}

	setFortnoxLinkedDocuments(bool) {
		this._isFortnoxLinkedDocumentsEnabled = bool;
	}

	isAgencyPortalLinkEnabled() {
		return this._isAgencyPortalLinkEnabled;
	}

	setAgencyPortalLinkEnabled(bool) {
		this._isAgencyPortalLinkEnabled = bool;
	}

	isAccountGroupsUsageInKpiEditorEnabled() {
		return this._isAccountGroupsUsageInKpiEditorEnabled;
	}

	setAccountGroupsUsageInKpiEditorEnabled(bool) {
		this._isAccountGroupsUsageInKpiEditorEnabled = bool;
	}

	isForecastingEnabled() {
		return this._isForecastingEnabled;
	}

	setForecasting(bool) {
		this._isForecastingEnabled = bool;
	}

	isArchiveCompanyEnabled() {
		return this._isArchiveCompanyEnabled;
	}

	setArchiveCompanyEnabled(bool) {
		this._isArchiveCompanyEnabled = bool;
	}

	isForecastToBudgetEnabled() {
		return this._isForecastToBudgetEnabled;
	}

	setForecastToBudgetEnabled(bool) {
		this._isForecastToBudgetEnabled = bool;
	}

	isEarlyAccessEnabled() {
		return this._isEarlyAccessEnabled;
	}

	setEarlyAccessEnabled(bool) {
		this._isEarlyAccessEnabled = bool;
	}

	isAdvancedThemingEnabled() {
		return this._isAdvancedThemingEnabled;
	}

	setAdvancedThemingEnabled(bool) {
		this._isAdvancedThemingEnabled = bool;
	}

	setId(newId) {
		if (this._id) {
			throw "Thou shalt not update short name of Agency";
		}
		this._id = newId;
	}

	toString() {
		return `${this._name}(${this._id})`;
	}

	deleteCompany(deletedCompany) {
		this._companies = this._companies.filter(
			(company) => company.getLegacyId() !== deletedCompany.getLegacyId(),
		);
	}

	getType() {
		return this.constructor.getType();
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.referentialApiId = this._referentialApiId;
		properties.shortName = this._shortName;
		properties.name = this._name;
		properties.homepage = this._homepage;
		properties.contactInfo = this._contactInfo;
		properties.countryId = this._countryId;
		properties.defaultLanguage = this._defaultLanguage;
		properties.defaultChartOfAccountsId = this._defaultChartOfAccountsId;
		properties.reportDisclaimer = this._reportDisclaimer;
		properties.agencyStatus = this._billingStatus.getType();
		properties.billingReference = this._billingReference;

		properties.displayNameForMailNotifications =
			this._displayNameForMailNotifications;

		properties.documentArchiveStructureAttachment =
			this._documentArchiveStructureAttachment;

		properties.isLiquidityEnabled = this._isLiquidityEnabled;
		properties.isBudgetEditorEnabled = this._isBudgetEditorEnabled;
		properties.isKeyFigureEditorEnabled = this._isKeyFigureEditorEnabled;
		properties.isDocumentsEnabled = this._isDocumentsEnabled;
		properties.isCustomReportsEnabled = this._isCustomReportsEnabled;
		properties.isCoAEditorEnabled = this._isCoAEditorEnabled;

		properties.isReportTemplateForStandardReportsEnabled =
			this._isReportTemplateForStandardReportsEnabled;

		properties.isBimestersEnabled = this._isBimestersEnabled;

		properties.isPeriodReportFeatureEnabled =
			this._isPeriodReportFeatureEnabled;

		properties.isAcknowledgePeriodReportEnabled =
			this._isAcknowledgePeriodReportEnabled;

		properties.isActivePeriodFeatureEnabled =
			this._isActivePeriodFeatureEnabled;

		properties.isPeriodReportNotificationFeatureEnabled =
			this._isPeriodReportNotificationFeatureEnabled;

		properties.isDocumentArchiveNotificationFeatureEnabled =
			this._isDocumentArchiveNotificationFeatureEnabled;

		properties.isReportDisclaimerEnabled = this._isReportDisclaimerEnabled;

		properties.isLrfkFarmerKeyFiguresEnabled =
			this._isLrfkFarmerKeyFiguresEnabled;

		properties.shouldRedirectToAgencyOnLogout =
			this._shouldRedirectToAgencyOnLogout;

		properties.isNavigationToMyCompaniesFromAccountingEnabled =
			this._isNavigationToMyCompaniesFromAccountingEnabled;

		properties.isManageUsersInUiDisabled = this._isManageUsersInUiDisabled;
		properties.isLightEnabled = this._isLightEnabled;

		properties.isShowPresentationEnabledForLightCompanies =
			this._isShowPresentationEnabledForLightCompanies;

		properties.isShowLightEnabledForStandardCompanies =
			this._isShowLightEnabledForStandardCompanies;

		properties.isProvisionedByOneClick = this._isProvisionedByOneClick;

		properties.isFortnoxLinkedDocumentsEnabled =
			this._isFortnoxLinkedDocumentsEnabled;

		properties.isAgencyPortalLinkEnabled = this._isAgencyPortalLinkEnabled;

		properties.isAccountGroupsUsageInKpiEditorEnabled =
			this._isAccountGroupsUsageInKpiEditorEnabled;

		properties.isForecastingEnabled = this._isForecastingEnabled;

		properties.isArchiveCompanyEnabled = this._isArchiveCompanyEnabled;

		properties.isForecastToBudgetEnabled = this._isForecastToBudgetEnabled;

		properties.isEarlyAccessEnabled = this._isEarlyAccessEnabled;

		properties.isAdvancedThemingEnabled = this._isAdvancedThemingEnabled;

		return properties;
	}

	static transformMaterializationData(data, context) {
		data.contactInfo = ContactInfo.materialize(data.contactInfo);
		data.defaultChartOfAccountsId = data.defaultChartOfAccountId;
		data.companyInfos ||= [];

		data.companies = Company.materializeAll(data.companyInfos, context);

		data.billingStatus = agencyBillingStatusFactory.materialize(
			data.agencyStatus,
		);

		data.brandingLogo &&= UploadedFile.materialize(data.brandingLogo);

		data.documentArchiveStructureAttachment &&= UploadedFile.materialize(
			data.documentArchiveStructureAttachment,
		);

		data.reportDisclaimer &&= ReportDisclaimer.materialize(
			data.reportDisclaimer,
		);
	}

	static getType() {
		return "Agency";
	}
}
